import i18n from "@/i18n";
import {
  VacancyApplicant,
  VacancyApplicantEmploymentStatusLevel,
  VacancyApplication,
} from "../../../model/VacancyApplicant.interface";
import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import { ActivityApplicationConstants } from "@/apps/tatar/activityApp/ActivityHooks";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import useVacancyApplicantActivityFlow from "../../../action-flow/hooks/useVacancyApplicantActivityFlow";
import moment from "moment";
import LoadOverlay from "@/components/LoadOverlay";
import { VacancyActionflowIndentifier } from "../../../model/VacancyActivity.interface";
import ModalManager from "@/components/ModalComponent/ModalManager";

type Props = {
  application: VacancyApplication;
  applicant: VacancyApplicant;
  activity: APActivity;
  constants: ActivityApplicationConstants;
  level?: VacancyApplicantEmploymentStatusLevel;
  onOpenGuarantorModal?: () => void;
  closeModal?: () => void;
};

const VacancyApplicantEmployerActions = (props: Props) => {
  const {
    application,
    applicant,
    activity,
    constants,
    level,
    onOpenGuarantorModal,
    closeModal,
  } = props;

  const {
    createGuarantorActionFlow,
    isLoading,
    openRentCoverageActionFlowModal,
  } = useVacancyApplicantActivityFlow({
    activity,
    constants,
  });

  const isGuarantorActionflow =
    applicant.currentActionFlow?.identifier ===
    VacancyActionflowIndentifier.Guarantor;

  const hasRentCoverageAttachment =
    applicant?.rentCoverageCertificate !== null &&
    applicant?.rentCoverageCertificate !== undefined;

  if (level.isRentCoverageNeeded && hasRentCoverageAttachment) {
    return null;
  }

  return (
    <div>
      <LoadOverlay open={isLoading} />
      <div>{level.label}</div>
      {level?.isGuarantorNeeded && (
        <div>
          {applicant.currentActionFlow?.status === "open" &&
          isGuarantorActionflow ? (
            <div>
              {i18n.t(
                "apTemplate:Vacancy.Applicant.Label.GuarantorRequested",
                "Bürgschaft angefordert am"
              )}{" "}
              {moment(applicant.currentActionFlow.date).format(
                i18n.t("Formats.dateFormat")
              )}
            </div>
          ) : applicant.guarantor ? (
            <div>
              <BFButton
                appearance="link"
                onClick={() => {
                  onOpenGuarantorModal();
                }}
              >
                {i18n.t(
                  "apTemplate:Vacancy.Applicant.Label.ShowGuarantor",
                  "Bürgen anzeigen"
                )}
              </BFButton>
            </div>
          ) : (
            <BFButton
              appearance="link"
              onClick={() => {
                ModalManager.confirm({
                  title: i18n.t(
                    "apTemplate:Vacancy.Applicant.Label.GuarantorNeeded",
                    "Bürgschaft anfordern"
                  ),
                  message: i18n.t(
                    "apTemplate:Vacancy.Applicant.Label.GuarantorNeededMessage",
                    "Möchten Sie eine Bürgschaft anfordern?"
                  ),
                  confirmButtonText: i18n.t(
                    "apTemplate:Vacancy.Applicant.Label.GuarantorNeeded",
                    "Bürgschaft anfordern"
                  ),
                  onConfirm: () => {
                    createGuarantorActionFlow(application, applicant.id);
                  },
                });
              }}
            >
              {i18n.t(
                "apTemplate:Vacancy.Applicant.Label.GuarantorNeeded",
                "Bürgschaft anfordern"
              )}
            </BFButton>
          )}
        </div>
      )}
      {level.isRentCoverageNeeded && (
        <div>
          {applicant.currentActionFlow?.status === "open" ? (
            <div>
              {i18n.t(
                "apTemplate:Vacancy.Applicant.Label.RentCoverageRequested",
                "Mietangebot gesendet am"
              )}{" "}
              {moment(applicant.currentActionFlow.date).format(
                i18n.t("Formats.dateFormat")
              )}
            </div>
          ) : (
            <BFButton
              appearance="link"
              onClick={() => {
                openRentCoverageActionFlowModal(application, applicant, () => {
                  closeModal?.();
                });
              }}
            >
              {i18n.t(
                "apTemplate:Vacancy.Applicant.Label.RentCoverageNeeded",
                "Mietbescheinigung anfordern"
              )}
            </BFButton>
          )}
        </div>
      )}
    </div>
  );
};

export default VacancyApplicantEmployerActions;
